import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Grid,
    Chip,
    Stack,
    List,
    ListItem,
    IconButton,
    ListItemButton,
    ListItemText,
    Button,
    Collapse,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import bfAlgoRuleService from "@plannel/services/dp/bf-algo-rule-service";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { alpha } from "@mui/material/styles";
import "@plannel/scss/components/DataLoadValidation.scss";
import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import itemService from "@plannel/services/data/item-service";
import reduxUtil from "@plannel/utils/redux-util";
import customerHrchyService from "@plannel/services/data/customer-hrchy-service";
import { isEqual } from "lodash";
import { useDispatch } from "react-redux";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import { withTranslation } from "react-i18next";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import HrchyLevelTreeSelect, { rollupHrchyNames } from "@plannel/components/filter/HrchyLevelTreeSelect";

/* ------------------------------ 스타일 ------------------------------ */
/*
 * 이 패널은 **뷰포트가 아니라 제 폭으로** 배치를 정한다.
 * BF 설정 화면에 얹히면 좌우 고정 패널(내비 220 + 요약 340)에 눌려 본문이 300px 대까지
 * 내려간다 — 뷰포트는 1000px 이 넘는데 칸은 스마트폰만 한 구간이 생긴다. MUI 의 xs/sm 으로
 * 끊으면 그 구간이 통째로 "넓은 화면" 으로 분류돼, 반 칸(xs={6})짜리 상자 두 개가 서로
 * **겹쳐 찍혔다.**
 */
const PANEL_NARROW = "@container (max-width: 420px)";
/* 반 칸(xs={6}) 을 좁을 때 한 칸으로 편다. MUI Grid v1 은 flexBasis/maxWidth 로 폭을 주므로 둘 다 덮는다. */
const HALF_TO_FULL_SX = { [PANEL_NARROW]: { flexBasis: "100%", maxWidth: "100%" } };

const fieldControlSx = {
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: 40 },
    /*
     * 트리 선택(HrchyLevelTreeSelect)이 제 FormControl 에 minWidth:230 을 박아 둬서, 칸이 그보다
     * 좁아지면 줄어드는 대신 **옆 칸 위로 삐져나가 겹쳐 찍혔다**(반 칸짜리 두 개가 그렇게 겹쳤다).
     * 여기서 풀어 준다 — 바깥 FormControl 이 fullWidth 라 폭은 그쪽이 이미 정한다.
     * ⚠️ 컴포넌트의 styles prop 으로 넘기지 않는다: 그 prop 은 트리거뿐 아니라 **드롭다운 목록**에도
     *   그대로 퍼져서, 목록 폭(minWidth:230)까지 같이 무너진다. 팝오버는 portal 이라 이 선택자엔 안 걸린다.
     */
    "& .MuiFormControl-root": { minWidth: 0 },
    "& .pln-tree-trigger, & .pln-tree-input, & .pln-tree-root": {
        height: "auto",
        minHeight: 40,
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
    },
};

/* ------------------------------ 유틸 ------------------------------ */
// 이름 기준 정렬
const byName = (a, b) =>
    String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
    });

const uniqByHrchyCd = (arr = []) => {
    const uniqueMap = new Map();
    for (const x of arr) {
        const cd = x?.hrchyCd;
        if (cd && !uniqueMap.has(cd)) uniqueMap.set(cd, x);
    }
    return [...uniqueMap.values()];
};
const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const toHrchyObjs = (rawList, refList) => {
    const out = [];
    const seen = new Set();
    for (const v of toArray(rawList)) {
        const cd = typeof v === "string" ? v : v?.hrchyCd;
        if (!cd || seen.has(cd)) continue;
        const obj =
            refList.find((h) => h.hrchyCd === cd) ??
            (typeof v === "object" && v?.hrchyCd ? v : { hrchyCd: cd, name: String(cd), leafFlg: true });
        seen.add(cd);
        out.push(obj);
    }
    return out;
};
const serializeModels = (arr = []) => [...arr].sort().join("|");

function getEnabledAlgoSet(state) {
    if (!state) return null;

    if (Array.isArray(state)) {
        const codes = state
            .map((x) => (typeof x === "string" ? x : x?.cd))
            .filter(Boolean);
        return codes.length ? new Set(codes) : null;
    }

    if (typeof state === "object") {
        const entries = Object.entries(state);
        if (entries.length === 0) return null;
        const codes = entries.filter(([, v]) => Boolean(v)).map(([k]) => k);
        return codes.length ? new Set(codes) : null;
    }

    return null;
}

/* =================================================================== */
const BfAlgorithmSettings = ({
                                 t = (s) => s,
                                 initialRules = [],
                                 registerGetRules,
                                 algorithmState,
                                 targetHrchy,
                                 stickyTop = 88,
                                 uploadRules = [],      // 정확 지정 룰(CSV 가져오기 유래) — 요약 표시 전용
                                 onRulesChanged,        // 삭제 후 재로드 콜백
                                 onRuleCountChange,     // 룰 개수 변화 통지(부모 요약용)
                                 rulesReloadKey,        // 부모가 저장 후 목록을 다시 읽으면 증가 — 카드를 저장 행 기준으로 재구성
                                 title,                 // 패널 제목. 기본은 "고급 설정"
                                 containerSx,           // 바깥 셸 덮어쓰기 — 스크롤 본문 안에 끼워 넣을 때 sticky 를 끈다
                             }) => {
    const viewName = "bfSettings";

    const dispatch = useDispatch();
    const { algorithmColor } = colorUtil;
    const { hrchyNum, customer: CUSTOMER, item: ITEM } = codeUtil;
    const reduxViewState = reduxUtil.getViewState(viewName) ?? {};

    /* ------------------------------ 원천/상태 ------------------------------ */
    const [itemHrchies, setItemHrchies] = useState(reduxViewState.itemHrchies ?? []);
    const [customerHrchies, setCustomerHrchies] = useState(reduxViewState.customerHrchies ?? []);
    const [itemLevels, setItemLevels] = useState(reduxViewState.itemLevels ?? []);
    const [custLevels, setCustLevels] = useState(reduxViewState.custLevels ?? []);

    const [custLevel, setCustLevel] = useState(reduxViewState.custLevel ?? null);
    const [itemLevel, setItemLevel] = useState(reduxViewState.itemLevel ?? null);

    const [selectedCustHrchies, setSelectedCustHrchies] = useState(() => {
        const legacy = reduxViewState.selectedCustHrchy ? [reduxViewState.selectedCustHrchy] : reduxViewState.selectedCustHrchies;
        return Array.isArray(legacy) ? legacy : [];
    });
    const [selectedItemHrchies, setSelectedItemHrchies] = useState(() => {
        const legacy = reduxViewState.selectedItemHrchy ? [reduxViewState.selectedItemHrchy] : reduxViewState.selectedItemHrchies;
        return Array.isArray(legacy) ? legacy : [];
    });

    const [itemFilterMode, setItemFilterMode] = useState("hierarchy"); // 'hierarchy' | 'new'
    const [npiChoice, setNpiChoice] = useState(true);
    const [bulkModels, setBulkModels] = useState([]);
    const [bfModelRules, setBfModelRules] = useState([]);
    const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
    const [showLevelMap, setShowLevelMap] = useState(true);

    // 현재 기준 활성 신제품 수(z_item.npi_flg='Y') — 아직 저장 전인 NPI 카드의 "신제품 없음" 안내용.
    // 저장된 카드의 건수는 서버 파생 appliedComboCount(현재 마스터 기준 거래처 x 품목 조합 수)를 쓴다.
    const [npiItemCount, setNpiItemCount] = useState(null);
    useEffect(() => {
        itemService.getNpiSummary()
            .then((response) => {
                const total = Number(response.data?.total);
                setNpiItemCount(Number.isFinite(total) ? total : null);
            })
            .catch((e) => console.debug(e));
    }, []);

    /* --------------------- 업로드 지정 룰 (정확 지정 — CSV 가져오기 화면에서 등록) --------------------- */
    // 정확 지정 룰을 (거래처레벨 × 품목레벨) → 모델세트별로 묶는다.
    // 레벨 탭과 무관하게 하단에 쌓이던 별도 블록을 없애고 해당 레벨의 카드로 합치기 위한 것.
    // 요약은 모델세트 축만 — 거래처 축까지 넣으면 조합 폭발 (설계 §7)
    const uploadGroupsByLevel = useMemo(() => {
        const custLvByKey = new Map([
            ...(custLevels ?? []).map((lv) => [String(lv.id ?? lv.cd), lv]),
            ...(custLevels ?? []).map((lv) => [lv.cd, lv]),
        ]);
        const itemLvByKey = new Map([
            ...(itemLevels ?? []).map((lv) => [String(lv.id ?? lv.cd), lv]),
            ...(itemLevels ?? []).map((lv) => [lv.cd, lv]),
        ]);
        // 키는 문자열로 통일 — jsonbig 파싱 경로에 따라 id 가 BigInt/객체로 섞여 Map.get 이 miss 난다
        const custById = new Map((customerHrchies ?? []).map((h) => [String(h.id), h]));
        const itemById = new Map((itemHrchies ?? []).map((h) => [String(h.id), h]));

        // 레벨 판정: 실체(ENTITY) 축과 범위가 하위 전체(leaf_scope='Y')인 계층 노드 축은 최하단 레벨,
        // 노드 자체(leaf_scope='N')만 그 노드의 hrchyLv 로 판정한다 — 규칙 관리 화면의 레벨 열과 같은 기준.
        // 모델세트 안에서 다시 "상위 조합"(실체는 부모 노드, 계층 노드는 자기 자신)으로 묶어
        // UI 카드(계층 × 계층 칩)와 같은 모양으로 보여준다. leftover 는 정의상 부분 커버라
        // 건수를 함께 표시한다 (전체 커버였다면 BfSettings 롤업이 이미 카드로 복원했다).
        const byLevel = new Map();
        for (const r of uploadRules ?? []) {
            const custCd = (r?.custNodeType === "ENTITY" || r?.customerLeafScope === "Y")
                ? CUSTOMER.cd
                : custLvByKey.get(String(custById.get(String(r?.customerNodeId))?.hrchyLv))?.cd ?? CUSTOMER.cd;
            const itemCd = (r?.itemNodeId == null || r?.itemNodeType === "ENTITY" || r?.itemLeafScope === "Y")
                ? ITEM.cd
                : itemLvByKey.get(String(itemById.get(String(r?.itemNodeId))?.hrchyLv))?.cd ?? ITEM.cd;

            const lvKey = `${custCd}__${itemCd}`;
            if (!byLevel.has(lvKey)) byLevel.set(lvKey, new Map());
            const byModels = byLevel.get(lvKey);
            const key = r?.models ?? "";
            if (!byModels.has(key)) byModels.set(key, []);
            byModels.get(key).push(r);
        }

        const comboOf = (r) => {
            const custEntity = r?.custNodeType === "ENTITY";
            const itemEntity = r?.itemNodeType === "ENTITY";
            const custKey = custEntity ? `P:${r?.custParentNodeId ?? "?"}` : `N:${r?.customerNodeId}`;
            const itemKey = r?.itemNodeId == null ? `NPI:${r?.npiFlg}` : itemEntity ? `P:${r?.itemParentNodeId ?? "?"}` : `N:${r?.itemNodeId}`;
            // 상위 조합 노드 객체 (실체 축은 부모 노드) — 라벨·전체 조합 수 산출용
            const custNode = custById.get(String(custEntity ? r?.custParentNodeId : r?.customerNodeId)) ?? null;
            const itemNode = r?.itemNodeId == null ? null
                : itemById.get(String(itemEntity ? r?.itemParentNodeId : r?.itemNodeId)) ?? null;
            return {
                key: `${custKey}|${itemKey}`,
                custNode,
                itemNode,
                custLabel: custEntity ? (r?.custParentName ?? r?.custParentCd ?? "-") : (r?.customerName ?? r?.customerCd ?? "-"),
                // NPI 행은 카드 칩과 같은 "신규 상품: true/false" 표기 (npiFlg 는 'Y'/'N' 문자열)
                itemLabel: r?.itemNodeId == null
                    ? `${t("newProduct") ?? "New Product"}: ${String(r?.npiFlg === true || r?.npiFlg === "Y")}`
                    : itemEntity ? (r?.itemParentName ?? r?.itemParentCd ?? "-") : (r?.itemName ?? r?.itemCd ?? "-"),
                // 부분 커버 표기용 전체 조합 수 — 실체 축의 부모 하위 개수 곱. 산출 불가면 null
                total: (() => {
                    const c = custEntity ? Number(r?.custParentChildCount) : 1;
                    const i = r?.itemNodeId == null ? 1 : itemEntity ? Number(r?.itemParentChildCount) : 1;
                    return Number.isFinite(c) && Number.isFinite(i) && c > 0 && i > 0 ? c * i : null;
                })(),
            };
        };

        const out = new Map();
        for (const [lvKey, byModels] of byLevel) {
            out.set(lvKey, [...byModels.entries()]
                .map(([models, rows]) => {
                    const comboMap = new Map();
                    for (const r of rows) {
                        const c = comboOf(r);
                        if (!comboMap.has(c.key)) comboMap.set(c.key, { ...c, rows: [] });
                        comboMap.get(c.key).rows.push(r);
                    }
                    const combos = [...comboMap.values()].sort((a, b) => b.rows.length - a.rows.length);
                    return { models, rows, combos };
                })
                .sort((a, b) => b.rows.length - a.rows.length));
        }
        return out;
    }, [uploadRules, custLevels, itemLevels, customerHrchies, itemHrchies, CUSTOMER.cd, ITEM.cd, t]);

    const handleDeleteUploadGroup = (rows) => {
        bfAlgoRuleService.remove(rows.map((r) => r.id)).then(() => onRulesChanged?.());
    };

    // 세부사항 → 수요예측 알고리즘 규칙 화면(DP_BF_ALGO_RULES). 그 화면은 룰을 전개한 거래처 x 품목 조합
    // 그리드라, 카드 한 줄의 저장 행(룰) id 들을 viewState 로 넘겨 그 룰들의 조합만 보이게 한다.
    // 검색값 주입은 **한 번의 updateViewState 로** 합친다(화면 링크 이동의 표준 패턴 — distNetworkNavActions 참고).
    // 탭 열기는 plannel:openMenu 로 — updateHistoryState 는 redux 값이 바뀌어야만 App 워처가 돌아
    // 같은 화면을 연달아 열면 이동이 씹힌다. 이 이벤트는 매번 동기로 발화한다(App 의 plannel:openMenu 리스너).
    const openRuleMgmt = (rows, label) => {
        const ids = (rows ?? []).map((r) => r?.id).filter((id) => id != null).map(String);   // jsonbig BigInt 안전
        if (!ids.length) return;
        dispatch(reduxUtil.updateViewState(reduxUtil.createObjectState("DP_BF_ALGO_RULES", {
            ruleIds: ids,
            ruleLabel: label,
            filter: {},
            models: [],
            customerSearch: "",
            itemSearch: "",
            currentPage: 1,
        })));
        window.dispatchEvent(new CustomEvent("plannel:openMenu", { detail: { key: "DP_BF_ALGO_RULES" } }));
    };
    const modelsLabel = (cds) => (cds ?? []).map((cd) => algoByCd[cd]?.name ?? cd).join(", ");

    const didInitRef = useRef(false);
    const didSeedRulesRef = useRef(false);

    const isCustBottom = isEqual(custLevel?.cd, CUSTOMER.cd);
    const isItemBottom = isEqual(itemLevel?.cd, ITEM.cd);
    const isBottomBoth = isCustBottom && isItemBottom;

    /* ------------------------------ 목록/필터 ------------------------------ */
    const leafFilteredCustHrchys = useMemo(() => {
        const list = (customerHrchies ?? []).filter((h) => h?.leafFlg === true);
        return list.slice().sort(byName);
    }, [customerHrchies]);

    const leafFilteredItemHrchys = useMemo(() => {
        const list = (itemHrchies ?? []).filter((h) => h?.leafFlg === true);
        return list.slice().sort(byName);
    }, [itemHrchies]);

    const filteredCustHrchys = useMemo(() => {
        if (!custLevel?.cd) return [];
        return custLevel.cd === CUSTOMER.cd
            ? leafFilteredCustHrchys
            : (customerHrchies ?? []).filter((h) => h.hrchyLv === custLevel.cd).sort(byName);
    }, [customerHrchies, custLevel?.cd, leafFilteredCustHrchys, CUSTOMER.cd]);

    const filteredItemHrchys = useMemo(() => {
        if (!itemLevel?.cd) return [];
        return itemLevel.cd === ITEM.cd
            ? leafFilteredItemHrchys
            : (itemHrchies ?? []).filter((h) => h.hrchyLv === itemLevel.cd).sort(byName);
    }, [itemHrchies, itemLevel?.cd, leafFilteredItemHrchys, ITEM.cd]);

    const selectedCustCds = useMemo(() => selectedCustHrchies.map((h) => h.hrchyCd), [selectedCustHrchies]);
    const selectedItemCds = useMemo(() => selectedItemHrchies.map((h) => h.hrchyCd), [selectedItemHrchies]);

    const applyFilter = useCallback((nextOrUpdater) => {
        setFilterValue(prev => {
              const next = typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
              const reduxState = reduxUtil.createFilterViewState(viewName, next);
              dispatch(reduxUtil.updateViewState(reduxState));
              return next;
            });
        }, [dispatch]);

    /* ------------------------------ 알고리즘 목록 ------------------------------ */
    const enabled = getEnabledAlgoSet(algorithmState);
    const allAlgos = Object.values(codeUtil.bfAlgorithm ?? {});
    const algoList = (enabled ? allAlgos.filter((a) => enabled.has(a.cd)) : allAlgos)
        .slice()
        .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));

    const algoByCd = useMemo(() => Object.fromEntries((algoList ?? []).map((a) => [a.cd, a])), [algoList]);
    // 이 규격이 원본이었고 같은 코드가 여러 곳에 복제돼 있어 colorUtil.tintChipSx 로 모았다.
    const chipSx = (name) => colorUtil.tintChipSx(algorithmColor?.[name]);

    /* ------------------------------ 현재 BF 모델(알고리즘) 사용 규칙 ------------------------------ */
    const comboIsSingle = useMemo(() => {
        if (!custLevel?.cd || !itemLevel?.cd) return false;
        if (isItemBottom && itemFilterMode === "new") return selectedCustCds.length === 1;
        return selectedCustCds.length === 1 && selectedItemCds.length === 1;
    }, [custLevel?.cd, itemLevel?.cd, isItemBottom, itemFilterMode, selectedCustCds.length, selectedItemCds.length]);

    const currentRule = useMemo(() => {
        if (!comboIsSingle) return { models: [] };
        const [custCd] = selectedCustCds;
        if (isItemBottom && itemFilterMode === "new") {
            return (
                bfModelRules.find(
                    (r) =>
                        r.custLevel?.cd === custLevel.cd &&
                        r.itemLevel?.cd === itemLevel.cd &&
                        r.custHrchy?.hrchyCd === custCd &&
                        r.npi === npiChoice
                ) ?? { models: [] }
            );
        }
        const [itemCd] = selectedItemCds;
        return (
            bfModelRules.find(
                (r) =>
                    r.custLevel?.cd === custLevel.cd &&
                    r.itemLevel?.cd === itemLevel.cd &&
                    r.custHrchy?.hrchyCd === custCd &&
                    r.itemHrchy?.hrchyCd === itemCd &&
                    typeof r.npi !== "boolean"
            ) ?? { models: [] }
        );
    }, [comboIsSingle, selectedCustCds, selectedItemCds, bfModelRules, custLevel?.cd, itemLevel?.cd, isItemBottom, itemFilterMode, npiChoice]);

    /* ------------------------------ Level 허용/옵션 (BF Setting STEP1 대상 설정에 따름 ------------------------------ */
    const allowedCustTier = useMemo(() => {
        if (!targetHrchy) return { CT: true, CM: true, CB: true };
        return {
            CT: Object.entries(targetHrchy).some(([k, v]) => k.startsWith("CT") && v),
            CM: Object.entries(targetHrchy).some(([k, v]) => k.startsWith("CM") && v),
            CB: Object.entries(targetHrchy).some(([k, v]) => k.startsWith("CB") && v),
        };
    }, [targetHrchy]);
    const allowedItemTier = useMemo(() => {
        if (!targetHrchy) return { IT: true, IM: true, IB: true };
        return {
            IT: Object.entries(targetHrchy).some(([k, v]) => k.endsWith("IT") && v),
            IM: Object.entries(targetHrchy).some(([k, v]) => k.endsWith("IM") && v),
            IB: Object.entries(targetHrchy).some(([k, v]) => k.endsWith("IB") && v),
        };
    }, [targetHrchy]);

    const getCustTier = useCallback(
        (levelCd) => {
            if (levelCd === codeUtil.customer.cd) return "CB";
            if (!custLevels?.length) return "CM";
            const topCd = custLevels[0]?.cd;
            return levelCd === topCd ? "CT" : "CM";
        },
        [custLevels]
    );
    const getItemTier = useCallback(
        (levelCd) => {
            if (levelCd === codeUtil.item.cd) return "IB";
            if (!itemLevels?.length) return "IM";
            const topCd = itemLevels[0]?.cd;
            return levelCd === topCd ? "IT" : "IM";
        },
        [itemLevels]
    );

    const custLevelOptions = useMemo(
        () => (custLevels ?? []).filter((lv) => allowedCustTier[getCustTier(lv.cd)]),
        [custLevels, allowedCustTier, getCustTier]
    );
    const itemLevelOptions = useMemo(
        () => (itemLevels ?? []).filter((lv) => allowedItemTier[getItemTier(lv.cd)]),
        [itemLevels, allowedItemTier, getItemTier]
    );

    // Item, Customer 계층이 모두 선택되어야 모델 선택 가능하도록
    const isCustItemSelectionValid = useMemo(() => {
        if (!custLevel?.cd || !itemLevel?.cd) return false;

        if (isItemBottom) {
            return itemFilterMode === "new"
                ? selectedCustCds.length > 0
                : selectedCustCds.length > 0 && selectedItemCds.length > 0;
        }
        return selectedCustCds.length > 0 && selectedItemCds.length > 0;
    }, [custLevel?.cd, itemLevel?.cd, isItemBottom, itemFilterMode, selectedCustCds.length, selectedItemCds.length]);

    const itemDefaultValue = useMemo(() => {
        const itemValue = filterValue?.category ?? filterValue?.itemCheckSelected ?? [];
        return Array.isArray(itemValue) ? itemValue : itemValue ? [itemValue] : [];
        }, [filterValue?.category, filterValue?.itemCheckSelected]);

    const customerDefaultValue = useMemo(() => {
        return toHrchyObjs(
              filterValue?.customerHrchys ?? filterValue?.customerCheckSelected ?? [],
              leafFilteredCustHrchys
            );
        }, [filterValue?.customerHrchys, filterValue?.customerCheckSelected, leafFilteredCustHrchys]);

    /* ------------------------------ 규칙 UI 변환 ------------------------------ */
    const toUiRules = useCallback(
        (rules) => {
            if (!Array.isArray(rules)) return [];
            // 이미 UI 형태면 그대로
            if (rules.length && (rules[0]?.custHrchy || rules[0]?.itemHrchy || rules[0]?.custLevel || rules[0]?.itemLevel)) return rules;

            // hrchy: id 기반 조회 (id를 key로 인덱싱)
            const custByHrchyId = Object.fromEntries((customerHrchies ?? []).map((h) => [h.id, h]));
            const itemByHrchyId = Object.fromEntries((itemHrchies ?? []).map((h) => [h.id, h]));

            // level: HrchyConfig는 id 보유, CUSTOMER/ITEM 상수는 id 없이 cd만 보유
            // → id와 cd 모두로 조회 가능하도록 Map 구성
            const custLvByKey = new Map([
                ...(custLevels ?? []).map((lv) => [String(lv.id ?? lv.cd), lv]),
                ...(custLevels ?? []).map((lv) => [lv.cd, lv]),
            ]);
            const itemLvByKey = new Map([
                ...(itemLevels ?? []).map((lv) => [String(lv.id ?? lv.cd), lv]),
                ...(itemLevels ?? []).map((lv) => [lv.cd, lv]),
            ]);

            return rules.map((r) => {
                const custHrchyId = r?.custHrchyId;
                const itemHrchyId = r?.itemHrchyId;

                const hasNpi = typeof r?.npi === "boolean";
                const npi = hasNpi ? r.npi : undefined;

                // id 기반으로 hrchy 객체 조회.
                // 못 찾으면(실체 = 실거래처·실품목은 계층 목록에 없다) 서버가 조인해 준 이름을 쓰고,
                // 그것마저 없으면(= 대상이 삭제된 고아 룰, 파생 컬럼이 전부 NULL) "삭제된 대상"으로 적는다.
                // 예전에는 이 자리에 `String(id)` 를 넣어 칩에 18자리 숫자가 그대로 보였다.
                const custHrchy = custByHrchyId[custHrchyId] ?? (custHrchyId != null ? { id: custHrchyId, hrchyCd: String(custHrchyId), name: r?.custHrchyName ?? t("deletedNode") } : null);
                const itemHrchy = hasNpi ? null : (itemByHrchyId[itemHrchyId] ?? (itemHrchyId != null ? { id: itemHrchyId, hrchyCd: String(itemHrchyId), name: r?.itemHrchyName ?? t("deletedNode") } : null));

                // level: id(숫자) 또는 cd(CUSTOMER/ITEM 문자열) 모두 처리
                const custLevelKey = String(r?.custLevelId ?? custHrchy?.hrchyLv ?? CUSTOMER.cd);
                const itemLevelKey = String(r?.itemLevelId ?? (hasNpi ? ITEM.cd : (itemHrchy?.hrchyLv ?? ITEM.cd)));

                return {
                    custLevel: custLvByKey.get(custLevelKey) ?? { cd: custLevelKey, name: custLevelKey },
                    itemLevel: itemLvByKey.get(itemLevelKey) ?? { cd: itemLevelKey, name: itemLevelKey },
                    custHrchy,
                    itemHrchy,
                    npi,
                    models: Array.isArray(r?.models) ? r.models.slice() : [],
                    dbRows: Array.isArray(r?.dbRows) ? r.dbRows : undefined,
                };
            });
        },
        [customerHrchies, itemHrchies, custLevels, itemLevels, CUSTOMER, ITEM, t]
    );

    /* ------------------------------ 조작 핸들러 ------------------------------ */
    const applyModelsToSelectedCombos = (cds) => {
        const custs = selectedCustHrchies.slice();
        if (!custs.length || !custLevel?.cd || !itemLevel?.cd) return;

        const items = isItemBottom && itemFilterMode === "new" ? null : selectedItemHrchies.slice();
        if (!items && !(isItemBottom && itemFilterMode === "new")) return;

        setBfModelRules((prev) => {
            const keyOf = (r) =>
                `${r.custLevel?.cd}|${r.custHrchy?.hrchyCd}|${r.itemLevel?.cd}|${
                    typeof r.npi === "boolean" ? `__NPI__${r.npi}` : r.itemHrchy?.hrchyCd
                }`;

            const combos = [];
            if (items) {
                for (const ch of custs) for (const ih of items) combos.push([ch, ih]);
            }

            const targetKeys = new Set(
                items
                    ? combos.map(([ch, ih]) => `${custLevel.cd}|${ch.hrchyCd}|${itemLevel.cd}|${ih.hrchyCd}`)
                    : custs.map((ch) => `${custLevel.cd}|${ch.hrchyCd}|${itemLevel.cd}|__NPI__${npiChoice}`)
            );
            const exists = new Set(prev.map(keyOf));

            const updated = prev
                .map((r) => {
                    const k = keyOf(r);
                    if (!targetKeys.has(k)) return r;
                    if (cds.length === 0) return null;
                    return { ...r, models: cds };
                })
                .filter(Boolean);

                    if (items) {
                for (const [ch, ih] of combos) {
                    const k = `${custLevel.cd}|${ch.hrchyCd}|${itemLevel.cd}|${ih.hrchyCd}`;
                    if (!exists.has(k) && cds.length > 0) {
                        updated.push({
                            custLevel: custLevel,
                            itemLevel: itemLevel,
                            custHrchy: ch,
                            itemHrchy: ih,
                            npi: undefined,
                            models: cds,
                        });
                    }
                }
            } else {
                for (const ch of custs) {
                    const k = `${custLevel.cd}|${ch.hrchyCd}|${itemLevel.cd}|__NPI__${npiChoice}`;
                    if (!exists.has(k) && cds.length > 0) {
                        updated.push({
                            custLevel: custLevel,
                            itemLevel: itemLevel,
                            custHrchy: ch,
                            itemHrchy: null,
                            npi: npiChoice,
                            models: cds,
                        });
                    }
                }
            }
            return updated;
        });
    };

    const injectSMAIntoCustomerAllRule = () => {
        const SMA = "SMA";
        const topItemLv = itemLevels?.[0]?.cd; // 아이템 최상단 레벨
        const topCustLv = custLevels?.[0]?.cd; // 고객 최상단 레벨

        setBfModelRules((prev) => {
            // 1) 최하단-최하단 + (루트-루트) 인 기존 룰을 찾는다.
            const idx = prev.findIndex((r) => {
                if (r?.custLevel?.cd !== CUSTOMER.cd || r?.itemLevel?.cd !== ITEM.cd) return false;

                // "계층의 루트" 판정: 해당 계층의 최상단 레벨을 가리키는 노드인지 확인
                const isCustRoot = r?.custHrchy?.hrchyLv && r.custHrchy.hrchyLv === topCustLv;
                const isItemRoot = r?.itemHrchy?.hrchyLv && r.itemHrchy.hrchyLv === topItemLv;

                return Boolean(isCustRoot && isItemRoot);
            });

            // (1) 해당 룰이 없으면 스킵
            if (idx < 0) return prev;

            // (2) SMA가 없으면 추가
            const rule = prev[idx];
            const models = Array.isArray(rule.models) ? rule.models : [];
            if (models.includes(SMA)) return prev;

            const next = prev.slice();
            next[idx] = { ...rule, models: [...models, SMA] };
            return next;
        });
    };

    const handleAlgorithmsChange = (e) => {
        const raw = Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(",");
        const cds = Array.from(new Set(raw));
        const SMA = "SMA";
        const isBulk = !comboIsSingle;

        if (!isBottomBoth && cds.includes(SMA)) injectSMAIntoCustomerAllRule();

        if (isBulk) {
            setBulkModels(cds);
            applyModelsToSelectedCombos(cds);
            return;
        }

        const effCustCd = selectedCustCds[0];
        if (!effCustCd) return;

        if (isItemBottom && itemFilterMode === "new") {
            setBfModelRules((prev) => {
                const idx = prev.findIndex(
                    (r) =>
                        r.custLevel?.cd === custLevel?.cd &&
                        r.itemLevel?.cd === itemLevel?.cd &&
                        r.custHrchy?.hrchyCd === effCustCd &&
                        r.npi === npiChoice
                );
                if (cds.length === 0) {
                    if (idx >= 0) {
                        const next = prev.slice();
                        next.splice(idx, 1);
                        return next;
                    }
                    return prev;
                }
                if (idx >= 0) {
                    const next = prev.slice();
                    next[idx] = { ...next[idx], models: cds };
                    return next;
                }
                return [
                    ...prev,
                    {
                        custLevel: custLevel,
                        itemLevel: itemLevel,
                        custHrchy: selectedCustHrchies[0],
                        itemHrchy: null,
                        npi: npiChoice,
                        models: cds,
                    },
                ];
            });
            return;
        }

        const effItemCd = selectedItemCds[0];
        if (!effItemCd) return;

        setBfModelRules((prev) => {
            const idx = prev.findIndex(
                (r) =>
                    r.custLevel?.cd === custLevel?.cd &&
                    r.itemLevel?.cd === itemLevel?.cd &&
                    r.custHrchy?.hrchyCd === effCustCd &&
                    r.itemHrchy?.hrchyCd === effItemCd &&
                    typeof r.npi !== "boolean"
            );

            if (cds.length === 0) {
                if (idx >= 0) {
                    const next = prev.slice();
                    next.splice(idx, 1);
                    return next;
                }
                return prev;
            }

            if (idx >= 0) {
                const next = prev.slice();
                next[idx] = { ...next[idx], models: cds };
                return next;
            }

            return [
                ...prev,
                {
                    custLevel: custLevel,
                    itemLevel: itemLevel,
                    custHrchy: selectedCustHrchies[0],
                    itemHrchy: selectedItemHrchies[0],
                    npi: undefined,
                    models: cds,
                },
            ];
        });
    };

    const handleRemoveAlgo = (cd) => {
        if (comboIsSingle) {
            const selected = currentRule?.models ?? [];
            const next = selected.filter((v) => v !== cd);
            handleAlgorithmsChange({ target: { value: next } });
        } else {
            const next = (bulkModels ?? []).filter((v) => v !== cd);
            setBulkModels(next);
            applyModelsToSelectedCombos(next);
        }
    };

    /* ------------------------------ 그룹/레벨 맵 ------------------------------ */
    const groupsByLevel = useMemo(() => {
        const levelMap = new Map();
        for (let i = 0; i < bfModelRules.length; i++) {
            const rule = bfModelRules[i];
            const Lkey = `${rule?.custLevel?.cd ?? ""}__${rule?.itemLevel?.cd ?? ""}`;
            if (!levelMap.has(Lkey)) {
                levelMap.set(Lkey, { custLevel: rule?.custLevel, itemLevel: rule?.itemLevel, modelGroups: new Map() });
            }
            const levelEntry = levelMap.get(Lkey);

            const sig = serializeModels(rule?.models ?? []);
            if (!levelEntry.modelGroups.has(sig)) {
                levelEntry.modelGroups.set(sig, { models: Array.isArray(rule?.models) ? [...rule.models] : [], pairs: [], firstIndex: i });
            }
            const mg = levelEntry.modelGroups.get(sig);
            const dispItem =
                typeof rule?.npi === "boolean"
                    ? { hrchyCd: rule.npi ? "__NPI_TRUE__" : "__NPI_FALSE__", name: `${t("newProduct") ?? "New Product"}: ${String(rule.npi)}` }
                    : rule?.itemHrchy;
            mg.pairs.push({ custHrchy: rule?.custHrchy, itemHrchy: dispItem, rule, ruleIndex: i });
            mg.firstIndex = Math.min(mg.firstIndex, i);
        }

        // 화면은 항상 선택된 한 레벨만 보여준다. 카드 룰이 없어도 정확 지정 룰이 있으면 렌더한다
        // (정확 지정 룰이 탭과 무관하게 하단에 따로 쌓이던 문제를 없애기 위함).
        const onlyKey = `${custLevel?.cd ?? ""}__${itemLevel?.cd ?? ""}`;
        const base = levelMap.get(onlyKey);
        const modelGroups = base
            ? [...base.modelGroups.values()].sort((a, b) => a.firstIndex - b.firstIndex)
            : [];

        // NPI 룰은 엔진이 화면 순서와 무관하게 무조건 우선 적용한다(npi > hier 고정).
        // 카드도 상단에 고정해 표시와 실제 적용 순서를 일치시킨다 — hasNpi 카드는 순서
        // 이동 대상에서도 제외한다(아래 화살표 렌더·moveLevelGroupBlock 과 한 세트).
        modelGroups.forEach((g) => { g.hasNpi = g.pairs.some((p) => typeof p.rule?.npi === "boolean"); });
        modelGroups.sort((a, b) => (a.hasNpi === b.hasNpi ? a.firstIndex - b.firstIndex : a.hasNpi ? -1 : 1));

        // 같은 모델세트는 카드 하나 — 부분 커버(정확 지정) 조합을 동일 모델세트의 UI 카드에
        // 합류시키고, 대응 카드가 없는 모델세트만 별도 카드로 남긴다.
        const bySig = new Map(modelGroups.map((mg) => [serializeModels(mg.models), mg]));
        const uploadGroups = [];
        for (const u of uploadGroupsByLevel.get(onlyKey) ?? []) {
            const sig = (u.models || "").split(";").filter(Boolean).sort().join("|");
            const mg = bySig.get(sig);
            if (mg) {
                mg.uploadCombos = u.combos;
            } else {
                uploadGroups.push(u);
            }
        }

        if (!modelGroups.length && !uploadGroups.length) return [];

        return [{
            custLevel: base?.custLevel ?? custLevel,
            itemLevel: base?.itemLevel ?? itemLevel,
            modelGroups,
            uploadGroups,
        }];
    }, [bfModelRules, custLevel, itemLevel, uploadGroupsByLevel, t]);

    const levelStats = useMemo(() => {
        const map = new Map();
        for (const r of bfModelRules) {
            const key = `${r?.custLevel?.cd ?? ""}__${r?.itemLevel?.cd ?? ""}`;
            if (!map.has(key)) map.set(key, { rules: 0, combos: new Set(), sigs: new Set() });
            const entry = map.get(key);
            entry.rules += 1;
            const itemKey = typeof r?.npi === "boolean" ? `__NPI__${r.npi}` : r?.itemHrchy?.hrchyCd ?? "";
            entry.combos.add(`${r?.custHrchy?.hrchyCd ?? ""}__${itemKey}`);
            entry.sigs.add(serializeModels(r?.models ?? []));
        }
        // 정확 지정 룰(CSV 가져오기)도 같은 레벨에 카드로 렌더된다(groupsByLevel.uploadGroups).
        // 카드 룰만 세면 레벨 맵 숫자가 화면의 카드 수보다 적게 나온다 — 업로드 전용 모델세트 누락.
        // 시그니처 정규화는 groupsByLevel 의 합류 판정과 동일해야 같은 모델세트가 중복으로 세지 않는다.
        for (const [key, groups] of uploadGroupsByLevel) {
            if (!map.has(key)) map.set(key, { rules: 0, combos: new Set(), sigs: new Set() });
            const entry = map.get(key);
            for (const u of groups ?? []) {
                entry.rules += u.rows?.length ?? 0;
                for (const c of u.combos ?? []) entry.combos.add(c.key);
                entry.sigs.add((u.models || "").split(";").filter(Boolean).sort().join("|"));
            }
        }
        const out = new Map();
        for (const [k, v] of map) {
            out.set(k, { rules: v.rules, combos: v.combos.size, modelSets: v.sigs.has("") ? v.sigs.size - 1 : v.sigs.size });
        }
        return out;
    }, [bfModelRules, uploadGroupsByLevel]);

    const gridCols = useMemo(() => Math.max(1, itemLevelOptions.length), [itemLevelOptions.length]);

    const moveLevelGroupBlock = useCallback((levelCustCd, levelItemCd, sig, direction) => {
        setBfModelRules((prev) => {
            const annotated = prev.map((r, idx) => ({ r, idx }));
            const levelEntries = annotated.filter(({ r }) => r?.custLevel?.cd === levelCustCd && r?.itemLevel?.cd === levelItemCd);
            if (!levelEntries.length) return prev;

            const groups = [];
            const gmap = new Map();
            for (const e of levelEntries) {
                const sModels = serializeModels(e.r.models ?? []);
                if (!gmap.has(sModels)) {
                    const group = { sig: sModels, items: [] };
                    gmap.set(sModels, group);
                    groups.push(group);
                }
                gmap.get(sModels).items.push(e);
            }

            // NPI 룰이 섞인 그룹은 상단 고정(엔진이 무조건 우선)이라 이동 대상에서 제외한다.
            // 재조립 시 pinned 를 앞에 두어 배열 순서도 표시 순서(NPI 먼저)와 일치시킨다.
            const pinned = groups.filter((g) => g.items.some(({ r }) => typeof r?.npi === "boolean"));
            const movable = groups.filter((g) => !pinned.includes(g));

            const idx = movable.findIndex((g) => g.sig === sig);
            if (idx < 0 || (direction === "up" && idx === 0) || (direction === "down" && idx === movable.length - 1)) return prev;

            const swapWith = direction === "up" ? idx - 1 : idx + 1;
            const newMovable = movable.slice();
            const [moving] = newMovable.splice(idx, 1);
            newMovable.splice(swapWith, 0, moving);

            const reordered = [...pinned, ...newMovable].flatMap((g) => g.items.map((e) => e.r));
            const iter = reordered[Symbol.iterator]();
            return prev.map((r) => (r?.custLevel?.cd === levelCustCd && r?.itemLevel?.cd === levelItemCd ? iter.next().value : r));
        });
    }, []);

    /* ------------------------------ 초기 로드 & 룰 시드 ------------------------------ */
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        const fromRedux =
            (reduxViewState.itemHrchies?.length || 0) > 0 &&
            (reduxViewState.customerHrchies?.length || 0) > 0 &&
            (reduxViewState.itemLevels?.length || 0) > 0 &&
            (reduxViewState.custLevels?.length || 0) > 0;

        if (fromRedux) {
            if (!custLevel && reduxViewState.custLevels?.length) setCustLevel(reduxViewState.custLevels[0]);
            if (!itemLevel && reduxViewState.itemLevels?.length) setItemLevel(reduxViewState.itemLevels[0]);
            return;
        }

        (async () => {
            try {
                const [itemRes, custRes, cfgRes] = await Promise.all([
                    itemHrchyService.getAll({ searchFilters: { activeFlg: true } }),
                    customerHrchyService.getAll({ searchFilters: { activeFlg: true } }),
                    hrchyConfigService.getAll({ searchFilters: { hrchyNum: hrchyNum.HRCHY } }),
                ]);
                const items = itemRes.data?.results ?? [];
                const custs = custRes.data?.results ?? [];

                const rows = (cfgRes.data?.results ?? []).slice().sort((a, b) => a.seqNum - b.seqNum);
                const itemCfg = [], custCfg = [];
                for (const r of rows) (r.itemHrchyFlg ? itemCfg : custCfg).push(r);

                const itemLv = [...itemCfg, ITEM];
                const custLv = [...custCfg, CUSTOMER];

                setItemHrchies(items);
                setCustomerHrchies(custs);
                setItemLevels(itemLv);
                setCustLevels(custLv);

                setCustLevel((prev) => prev || (custLv[0] ?? null));
                setItemLevel((prev) => prev || (itemLv[0] ?? null));
            } catch (e) {
                console.debug(e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (didSeedRulesRef.current) return;
        if (!initialRules?.length) return;
        if (!customerHrchies.length || !itemHrchies.length || !custLevels.length || !itemLevels.length) return;

        setBfModelRules(toUiRules(initialRules));
        didSeedRulesRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRules, customerHrchies.length, itemHrchies.length, custLevels.length, itemLevels.length, toUiRules]);

    // 저장 직후 재구성. 저장 시점엔 로컬 카드와 DB 가 같은 내용이라 덮어써도 잃는 편집이 없고,
    // 저장 행(dbRows)이 붙어 조합 건수가 새로고침 없이 바로 보인다.
    useEffect(() => {
        if (!rulesReloadKey) return;
        setBfModelRules(toUiRules(initialRules ?? []));
        didSeedRulesRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rulesReloadKey]);

    /* ------------------------------ 선택 유지 & 트리 동기화 ------------------------------ */
    useEffect(() => {
        // customer
        const custByCd = Object.fromEntries(filteredCustHrchys.map((h) => [h.hrchyCd, h]));
        const keptC = selectedCustHrchies.map((h) => custByCd[h?.hrchyCd]).filter(Boolean);
        if (keptC.length !== selectedCustHrchies.length || keptC.some((h, i) => h !== selectedCustHrchies[i])) {
            setSelectedCustHrchies(keptC);
        }

        // item
        const itemByCd = Object.fromEntries(filteredItemHrchys.map((h) => [h.hrchyCd, h]));
        const keptI = selectedItemHrchies.map((h) => itemByCd[h?.hrchyCd]).filter(Boolean);
        if (keptI.length !== selectedItemHrchies.length || keptI.some((h, i) => h !== selectedItemHrchies[i])) {
            setSelectedItemHrchies(keptI);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredCustHrchys, filteredItemHrchys, isCustBottom, isItemBottom]);

    // 하단 트리 → 상단 동기화 (최하단일 때만)
    useEffect(() => {
        // customer
        if (isCustBottom) {
            const custRaw = filterValue?.customerHrchys ?? filterValue?.customerCheckSelected ?? [];
            const custObjs = toHrchyObjs(custRaw, leafFilteredCustHrchys);
            const sameC = custObjs.length === selectedCustHrchies.length && custObjs.every((x, i) => x?.hrchyCd === selectedCustHrchies[i]?.hrchyCd);
            if (!sameC) setSelectedCustHrchies(custObjs);
        }

        // item
        if (isItemBottom) {
            if (itemFilterMode === "new") {
                if (selectedItemHrchies.length > 0) {
                    setSelectedItemHrchies([]);
                }
            } else {
                const itemRaw = filterValue?.category ?? filterValue?.itemCheckSelected ?? [];
                const itemObjs = toHrchyObjs(itemRaw, leafFilteredItemHrchys);
                const sameI = itemObjs.length === selectedItemHrchies.length && itemObjs.every((x, i) => x?.hrchyCd === selectedItemHrchies[i]?.hrchyCd);
                if (!sameI) setSelectedItemHrchies(itemObjs);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustBottom, isItemBottom, itemFilterMode, filterValue, leafFilteredCustHrchys, leafFilteredItemHrchys, selectedCustHrchies, selectedItemHrchies]);

    /* ------------------------------ 뷰 상태 저장/등록/알고리즘 정리 ------------------------------ */
    useEffect(() => {
        dispatch(
            reduxUtil.updateViewState(
                reduxUtil.createViewState("bfSettings", {
                    itemHrchies,
                    customerHrchies,
                    itemLevels,
                    custLevels,
                    custLevel,
                    itemLevel,
                    selectedCustHrchies,
                    selectedItemHrchies,
                    selectedCustHrchy: selectedCustHrchies[0] ?? null,
                    selectedItemHrchy: selectedItemHrchies[0] ?? null,
                    filter: filterValue,
                })
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemHrchies, customerHrchies, itemLevels, custLevels, custLevel, itemLevel, selectedCustHrchies, selectedItemHrchies]);

    useEffect(() => {
        registerGetRules?.(() => bfModelRules);
        // 부모(BF Settings 요약 칸)가 "룰 몇 건이 기본값을 덮어쓰는지"를 실시간으로 보여준다.
        onRuleCountChange?.(bfModelRules.length);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerGetRules, onRuleCountChange, bfModelRules]);

    useEffect(() => {
        if (!bfModelRules?.length) return;

        const enabled = getEnabledAlgoSet(algorithmState);
        if (!enabled) return;

        let changed = false;
        const next = [];
        for (const r of bfModelRules) {
            const prevModels = Array.isArray(r.models) ? r.models : [];
            const filtered = prevModels.filter((cd) => enabled.has(cd));
            if (filtered.length !== prevModels.length) changed = true;
            if (filtered.length > 0) next.push({ ...r, models: filtered });
        }
        if (changed) setBfModelRules(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [algorithmState, bfModelRules]);

    useEffect(() => {
        if (!isCustItemSelectionValid && (bulkModels?.length ?? 0) > 0) {
            setBulkModels([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustItemSelectionValid, bulkModels]);

    // 고객 레벨 변경 시: 자동 선택하지 않고, 현재 레벨에 없는 기존 선택만 정리
    // (사용자가 직접 고르기 전까지 계층 선택은 빈 값 유지)
    useEffect(() => {
        if (!custLevel?.cd) return;
        if (isCustBottom) return;

        setSelectedCustHrchies((prev) => {
            const kept = prev.filter((h) => h?.hrchyLv === custLevel.cd);
            return kept.length === prev.length ? prev : kept;
        });
    }, [custLevel?.cd, isCustBottom, customerHrchies]);

    // 아이템 레벨 변경 시: 자동 선택하지 않고, 현재 레벨에 없는 기존 선택만 정리
    useEffect(() => {
        if (!itemLevel?.cd) return;
        if (isItemBottom) return;

        setSelectedItemHrchies((prev) => {
            const kept = prev.filter((h) => h?.hrchyLv === itemLevel.cd);
            return kept.length === prev.length ? prev : kept;
        });
    }, [itemLevel?.cd, isItemBottom, itemHrchies]);

    useEffect(() => {
        // 현재 custLevel이 없거나, 옵션에 더 이상 존재하지 않으면 첫 번째로 보정
        if (!custLevel?.cd || !custLevelOptions.some(l => l.cd === custLevel.cd)) {
            const first = custLevelOptions?.[0] ?? null;
            if (first && first.cd !== custLevel?.cd) {
                setCustLevel(first);
                // 선택은 기존 useEffect에서 자동 세팅됨
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [custLevel?.cd, custLevelOptions]);

    useEffect(() => {
        if (!itemLevel?.cd || !itemLevelOptions.some(l => l.cd === itemLevel.cd)) {
            const first = itemLevelOptions?.[0] ?? null;
            if (first && first.cd !== itemLevel?.cd) {
                setItemLevel(first);
                // 선택은 기존 useEffect에서 자동 세팅됨
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemLevel?.cd, itemLevelOptions]);

    /* ------------------------------ UI ------------------------------ */
    return (
        <Box sx={{
            position: { md: "sticky" }, top: stickyTop,
            maxHeight: "calc(100vh - 120px)", overflow: "auto",
            borderRadius: 3, p: 2, border: (t) => `1px solid ${t.palette.divider}`,
            backgroundColor: (t) => t.palette.background.paper,
            // 아래 @container 질의들의 기준. containerSx 앞에 둬서 호출자가 필요하면 덮을 수 있게 한다.
            containerType: "inline-size",
            ...containerSx,
        }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{ fontWeight: 700, fontSize: 16 }}>
                    {title ?? t("advancedSettings")}
                </Box>
            </Box>

            {/* 레벨 */}
            <Typography variant="caption" sx={{ display: "block", mb: 1.5, fontWeight: 600, fontSize: 13 }}>
                {t("targetHierarchyLevel")}
            </Typography>
            <Grid container rowSpacing={1.2} columnSpacing={1.2}>
                {/* Customer Level */}
                <Grid item xs={6} sx={HALF_TO_FULL_SX}>
                    <FormControl fullWidth size="small" sx={fieldControlSx}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t("custLevel")}
                            select
                            SelectProps={{
                                value: custLevel?.cd ?? "",
                                onChange: (e) => {
                                    const cd = e.target.value;
                                    const lv = custLevelOptions.find((l) => l.cd === cd) || null;
                                    if (lv?.cd === custLevel?.cd) return;
                                    setCustLevel(lv);
                                    // 선택은 아래 useEffect에서 0번째 자동 세팅
                                    setSelectedCustHrchies([]);
                                },
                            }}
                        >
                            {custLevelOptions?.map((lv) => (
                                <MenuItem key={lv.cd} value={lv.cd}>
                                    {lv.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                </Grid>

                {/* Item Level */}
                <Grid item xs={6} sx={HALF_TO_FULL_SX}>
                    <FormControl fullWidth size="small" sx={fieldControlSx}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t("itemLevel")}
                            select
                            SelectProps={{
                                value: itemLevel?.cd ?? "",
                                onChange: (e) => {
                                    const cd = e.target.value;
                                    const lv = itemLevelOptions.find((l) => l.cd === cd) || null;
                                    if (lv?.cd === itemLevel?.cd) return;
                                    setItemLevel(lv);
                                    // 선택은 아래 useEffect에서 0번째 자동 세팅
                                    setSelectedItemHrchies([]);
                                    setItemFilterMode("hierarchy");
                                },
                            }}
                        >
                            {itemLevelOptions?.map((lv) => (
                                <MenuItem key={lv.cd} value={lv.cd}>
                                    {lv.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>
                </Grid>

                {/* Hierarchy 섹션 헤더 */}
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 13 }}>
                            {t("forecastHrchy")}
                        </Typography>
                        {isItemBottom && (
                            <ToggleButtonGroup
                                value={itemFilterMode}
                                exclusive
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setItemFilterMode(v);
                                    if (v === "new") setSelectedItemHrchies([]);
                                }}
                                size="small"
                                sx={{ "& .MuiToggleButton-root": { px: 1, py: 0.25, fontSize: 12, lineHeight: 1.1 } }}
                            >
                                <ToggleButton value="hierarchy">{t("hrchy") ?? "Hierarchy"}</ToggleButton>
                                <ToggleButton value="new">{t("newProduct") ?? "New Product"}</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Box>
                </Grid>

                {/* Customer Hierarchy */}
                <Grid item xs={6} sx={HALF_TO_FULL_SX}>
                    <FormControl fullWidth size="small" sx={fieldControlSx}>
                        {isCustBottom ? (
                            <CustomerHrchyTreeFilter
                                viewName={viewName}
                                onChangeFilter={setFilterValue}
                                defaultValue={customerDefaultValue}
                                enableSelectParent
                                enableMultiSelect
                                styles={{ width: "100%" }}
                            />
                        ) : (
                            <HrchyLevelTreeSelect
                                label={t("customerHrchy")}
                                hrchies={customerHrchies}
                                levelCd={custLevel?.cd}
                                selected={selectedCustHrchies}
                                onChange={setSelectedCustHrchies}
                                styles={{ width: "100%" }}
                            />
                        )}
                    </FormControl>
                </Grid>

                {/* Item Hierarchy or NPI true/false */}
                <Grid item xs={6} sx={HALF_TO_FULL_SX}>
                    <FormControl fullWidth size="small" sx={fieldControlSx}>
                        {isItemBottom && itemFilterMode === "hierarchy" ? (
                            <ItemHrchyTreeFilter
                                viewName={viewName}
                                onChangeFilter={setFilterValue}
                                defaultValue={itemDefaultValue}
                                enableSelectParent
                                enableMultiSelect
                                treeload
                                styles={{ width: "100%" }}
                            />
                        ) : isItemBottom && itemFilterMode === "new" ? (
                            <TextField
                                fullWidth
                                size="small"
                                label={t("newProduct") ?? "New Product"}
                                select
                                SelectProps={{
                                    value: npiChoice,
                                    onChange: (e) => setNpiChoice(e.target.value === true || e.target.value === "true"),
                                    multiple: false,
                                    renderValue: (v) => String(Boolean(v)),
                                }}
                            >
                                <MenuItem value={true}>
                                    <ListItemText primary="true" />
                                </MenuItem>
                                <MenuItem value={false}>
                                    <ListItemText primary="false" />
                                </MenuItem>
                            </TextField>
                        ) : (
                            <HrchyLevelTreeSelect
                                label={t("itemHrchy")}
                                hrchies={itemHrchies}
                                levelCd={itemLevel?.cd}
                                selected={selectedItemHrchies}
                                onChange={setSelectedItemHrchies}
                                styles={{ width: "100%" }}
                            />
                        )}
                    </FormControl>
                </Grid>
            </Grid>

            {/* 레벨 조합 맵 토글 */}
            <Box sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}>
                <Button size="small" variant="text" startIcon={showLevelMap ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => setShowLevelMap((v) => !v)}>
                    {t("detail")}
                </Button>
            </Box>

            {/* 레벨 조합 맵 */}
            <Collapse in={showLevelMap} timeout="auto" unmountOnExit>
                {/* overflowX — 레벨 조합 맵은 칸이 최소 64px 씩이라 레벨이 늘면 어떤 폭에서도 결국 넘친다.
                    그때 **이 상자 안에서** 가로로 밀리게 한다: 이게 없으면 넘친 폭이 본문 전체로 번져
                    화면 바닥에 가로 스크롤바가 생기고, 상관없는 카드들까지 같이 옆으로 밀려 나갔다. */}
                <Box sx={{ mt: 0.75, overflowX: "auto" }}>
                    {itemLevelOptions.length > 0 && custLevelOptions.length > 0 ? (
                        <Box sx={{
                            display: "grid", gap: 0.25, alignItems: "stretch",
                            gridTemplateColumns: `110px repeat(${gridCols}, minmax(64px, 1fr))`,
                            /* 좁아지면 레벨 이름 칸부터 줄인다 — 값 칸(숫자 한 자)보다 줄일 여지가 많다 */
                            [PANEL_NARROW]: { gridTemplateColumns: `80px repeat(${gridCols}, minmax(64px, 1fr))` },
                        }}>
                            <Box />
                            {itemLevelOptions.map((il) => (
                                <Box key={`ihdr_${il.cd}`} sx={{ p: 0.5, borderRadius: 1, textAlign: "center", fontWeight: 700, fontSize: 11, bgcolor: "action.hover", lineHeight: 1.2 }}>
                                    {il.name}
                                </Box>
                            ))}

                            {custLevelOptions.map((cl) => (
                                <Box key={`row_${cl.cd}`} sx={{ display: "contents" }}>
                                    <Box sx={{ p: 0.5, borderRadius: 1, fontWeight: 700, fontSize: 11, bgcolor: "action.hover", lineHeight: 1.2 }}>{cl.name}</Box>
                                    {itemLevelOptions.map((il) => {
                                        const key = `${cl.cd}__${il.cd}`;
                                        const stat = levelStats.get(key);
                                        const groups = stat?.modelSets ?? 0;
                                        const hasRules = groups > 0;
                                        const isActive = custLevel?.cd === cl.cd && itemLevel?.cd === il.cd;
                                        return (
                                                <Box key={`cell_${key}`}
                                                     onClick={() => {
                                                         const nextCustLv = cl;
                                                         const nextItemLv = il;

                                                         if (custLevel?.cd !== nextCustLv.cd) setCustLevel(nextCustLv);
                                                         if (itemLevel?.cd !== nextItemLv.cd) setItemLevel(nextItemLv);

                                                         // 1) 해당 레벨 조합의 첫 번째 규칙 찾기 (bfModelRules의 순서를 신뢰)
                                                         const firstIdx = bfModelRules.findIndex(
                                                             (r) => r?.custLevel?.cd === nextCustLv.cd && r?.itemLevel?.cd === nextItemLv.cd
                                                         );

                                                         if (firstIdx >= 0) {
                                                             const first = bfModelRules[firstIdx];
                                                             const isNpi = typeof first?.npi === "boolean";
                                                             // 규칙이 있어도 계층 선택은 자동으로 채우지 않는다 —
                                                             // 사용자가 직접 선택하기 전까지 빈 값 유지 (모드만 규칙에 맞춤)
                                                             if (isNpi) {
                                                                 setItemFilterMode("new");
                                                                 setNpiChoice(Boolean(first.npi));
                                                             } else {
                                                                 setItemFilterMode("hierarchy");
                                                             }
                                                         } else {
                                                             setItemFilterMode("hierarchy");
                                                         }

                                                         // 사용자가 직접 선택하기 전까지 계층 선택은 빈 값 유지
                                                         setSelectedCustHrchies([]);
                                                         setSelectedItemHrchies([]);

                                                         applyFilter(prev => {
                                                             const out = { ...prev };
                                                             if (nextCustLv.cd === CUSTOMER.cd) {
                                                                 out.customerHrchys = [];
                                                                 out.customerCheckSelected = [];
                                                             } else {
                                                                 delete out.customerHrchys;
                                                                 delete out.customerCheckSelected;
                                                             }
                                                             if (nextItemLv.cd === ITEM.cd) {
                                                                 out.category = [];
                                                                 out.itemCheckSelected = [];
                                                             } else {
                                                                 delete out.category;
                                                                 delete out.itemCheckSelected;
                                                             }
                                                             return out;
                                                         });
                                                     }}
                                                    sx={{
                                                        p: 0.5,
                                                        borderRadius: 1,
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                        userSelect: "none",
                                                        fontWeight: 700,
                                                        fontSize: 12,
                                                        border: (t) => (isActive ? `1px solid ${t.palette.primary.main}` : `1px solid ${t.palette.divider}`),
                                                        bgcolor: (t) => (hasRules ? alpha(t.palette.primary.main, 0.08) : t.palette.background.default),
                                                        color: hasRules ? "text.primary" : "text.disabled",
                                                        lineHeight: 1.1,
                                                        transition: "all .15s ease",
                                                        "&:hover": { bgcolor: (t) => (hasRules ? alpha(t.palette.primary.main, 0.14) : t.palette.action.hover) },
                                                    }}
                                                >
                                                    {groups}
                                                </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ p: 0.75, borderRadius: 1, bgcolor: "action.hover", color: "text.secondary", fontSize: 12 }}>{"No Levels."}</Box>
                    )}
                </Box>
            </Collapse>

            {/* Algorithms */}
            {/*
              * 폭을 묶는다. xs=12 + fullWidth 라 카드가 넓어지는 만큼 끝없이 늘어나는데,
              * 담기는 건 알고리즘 칩 몇 개라 넓힐수록 빈칸만 커진다.
              * 칩이 많으면 여러 줄로 접히는데 그건 괜찮다 — 칸이 세로로 늘 뿐이고(minHeight),
              * 빈 채로 가로로 길게 누워 있는 것보다 낫다.
              */}
            <Grid container spacing={1.2} sx={{ mt: 0.5, maxWidth: 440 }}>
                <Grid item xs={12}>
                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "text.secondary" }}>
                        {t("algorithm")}
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ minHeight: 37 }}>
                        <Select
                            multiple
                            value={
                                comboIsSingle
                                    ? (currentRule?.models ?? [])
                                    : (isCustItemSelectionValid ? (bulkModels ?? []) : [])
                            }
                            disabled={!comboIsSingle && !isCustItemSelectionValid}
                            onChange={handleAlgorithmsChange}
                            displayEmpty
                            renderValue={(selected) =>
                                (selected?.length ?? 0) === 0 ? (
                                    t("forecastMethod")
                                ) : (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {selected.map((cd) => (
                                            <Chip
                                                key={cd}
                                                label={algoByCd[cd]?.name ?? cd}
                                                size="small"
                                                sx={chipSx(algoByCd[cd]?.name)}
                                                onDelete={() => handleRemoveAlgo(cd)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            />
                                        ))}
                                    </Box>
                                )
                            }
                        >
                            {(algoList ?? []).map((algo) => (
                                <MenuItem key={algo.cd} value={algo.cd}>
                                    {algo.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* 하단 규칙 목록.
                고른 레벨 짝에 룰이 없으면 300px 짜리 빈 상자만 남아 화면이 텅 비어 보였다 —
                그럴 땐 상자를 한 줄로 줄이고 왜 비었는지 적는다. */}
            <Box sx={{
                mt: 1.5,
                minHeight: groupsByLevel.length ? 300 : 0,
                maxHeight: 300,
                overflow: 'auto',
                borderRadius: 2,
                // 비었을 땐 상자 자체를 지운다 — 안내 한 줄만 남기면 되는데 점선 상자까지 있으면 겹만 늘어난다
                bgcolor: (t) => (groupsByLevel.length ? t.palette.action.hover : 'transparent'),
                border: (t) => (groupsByLevel.length ? `1px dashed ${t.palette.divider}` : 'none'),
                p: groupsByLevel.length ? 1.25 : 0 }}>
                <List dense disablePadding>
                    {groupsByLevel.map((L) => (
                        <ListItem key={`${L?.custLevel?.cd}|${L?.itemLevel?.cd}`} disableGutters sx={{ display: "block", px: 0, py: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                                {L?.custLevel?.name} — {L?.itemLevel?.name}
                            </Typography>

                            {L.modelGroups.map((g, gIdx) => {
                                const sig = serializeModels(g?.models);
                                const totalGroups = L.modelGroups.length;
                                // NPI 카드는 상단 고정(groupsByLevel 정렬)이라 이동 불가.
                                // 일반 카드의 상한은 첫 번째 일반 카드 위치(= NPI 카드 수).
                                const pinnedCount = L.modelGroups.filter((x) => x.hasNpi).length;
                                const canUp = gIdx > pinnedCount;
                                const canDown = gIdx < totalGroups - 1;

                                return (
                                    <Box key={`${L?.custLevel?.cd}|${L?.itemLevel?.cd}|${sig}`} sx={{ borderRadius: 1.5, border: (t) => `1px solid ${t.palette.divider}`, p: 1, mb: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                {g.models.map((cd) => (
                                                    <Chip
                                                        key={cd}
                                                        label={algoByCd[cd]?.name ?? cd}
                                                        size="small"
                                                        sx={chipSx(algoByCd[cd]?.name)}
                                                        onDelete={() => {
                                                            setBfModelRules((prev) =>
                                                                prev
                                                                    .map((r) => {
                                                                        const matchLevel = r.custLevel?.cd === L.custLevel?.cd && r.itemLevel?.cd === L.itemLevel?.cd;
                                                                        const matchInGroup = serializeModels(r.models ?? []) === sig;
                                                                        if (!(matchLevel && matchInGroup)) return r;
                                                                        return { ...r, models: (r.models ?? []).filter((m) => m !== cd) };
                                                                    })
                                                                    .filter((r) => (r.models ?? []).length > 0)
                                                            );
                                                        }}
                                                    />
                                                ))}
                                            </Stack>

                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                {/* NPI 카드는 항상 우선이라 순서 이동 화살표 자체를 숨긴다 */}
                                                {!g.hasNpi && (
                                                    <>
                                                        <IconButton size="small" onClick={() => moveLevelGroupBlock(L.custLevel?.cd, L.itemLevel?.cd, sig, "up")} disabled={!canUp} aria-label="move group up">
                                                            <ArrowUpwardIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => moveLevelGroupBlock(L.custLevel?.cd, L.itemLevel?.cd, sig, "down")} disabled={!canDown} aria-label="move group down">
                                                            <ArrowDownwardIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setBfModelRules((prev) =>
                                                            prev.filter((r) => {
                                                                const matchLevel = r.custLevel?.cd === L.custLevel?.cd && r.itemLevel?.cd === L.itemLevel?.cd;
                                                                const matchGroup = serializeModels(r.models ?? []) === sig;
                                                                return !(matchLevel && matchGroup);
                                                            })
                                                        );
                                                    }}
                                                    aria-label="delete group"
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        {(() => {
                                            // 조합 행 — 품목 구성이 같은 고객끼리 묶어 한 행으로 표시하되,
                                            // 부분 커버 행과 동일하게 행마다 건수(n / 전체)·세부사항·삭제를 붙인다.
                                            // 전체 커버 행은 n === 전체 (예: 182 / 182).
                                            const byCust = new Map();
                                            for (const p of g.pairs) {
                                                const k = p.custHrchy?.hrchyCd ?? "__";
                                                if (!byCust.has(k)) byCust.set(k, { cust: p.custHrchy, pairs: [] });
                                                byCust.get(k).pairs.push(p);
                                            }
                                            const bySig = new Map();
                                            for (const gc of byCust.values()) {
                                                const itemSig = gc.pairs.map((p) => p.itemHrchy?.hrchyCd ?? "-").sort().join("|");
                                                if (!bySig.has(itemSig)) bySig.set(itemSig, { custs: [], items: gc.pairs.map((p) => p.itemHrchy), pairs: [] });
                                                const e = bySig.get(itemSig);
                                                e.custs.push(gc.cust);
                                                e.pairs.push(...gc.pairs);
                                            }
                                            return [...bySig.values()].map((line, lineIdx) => {
                                                const dbRows = line.pairs.flatMap((p) => p.rule?.dbRows ?? []);
                                                const hasDb = dbRows.length > 0;
                                                const custLabels = rollupHrchyNames(customerHrchies, L.custLevel?.cd, line.custs, L.custLevel?.cd === CUSTOMER.cd);
                                                const itemLabels = rollupHrchyNames(itemHrchies, L.itemLevel?.cd, line.items, L.itemLevel?.cd === ITEM.cd);
                                                // 건수 = 서버가 현재 마스터 기준으로 계산한 적용 조합 수(appliedComboCount) 합.
                                                // 범위 룰은 저장 후 추가된 거래처·품목도 덮으므로 저장 행 수가 아니라 이 값을 보여준다.
                                                // NPI 룰에 신제품이 0건이면 "0 / 0" 대신 없음을 알린다 — 룰은 유지되고 신제품이 등록되면 바로 적용된다.
                                                const npiVal = line.pairs.find((p) => typeof p.rule?.npi === "boolean")?.rule?.npi;
                                                const applied = hasDb ? dbRows.reduce((s, r) => s + Number(r.appliedComboCount ?? 1), 0) : null;
                                                const noNpiItems = npiVal === true && (hasDb ? applied === 0 : npiItemCount === 0);
                                                const countText = noNpiItems
                                                    ? t("noNewProduct")
                                                    : hasDb ? `${applied} / ${applied}` : line.pairs.length;
                                                return (
                                                    <Box key={`line-${lineIdx}`} sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                        <ListItemButton
                                                            onClick={() => {
                                                                const nextCustLv = custLevels.find((lv) => lv.cd === L.custLevel?.cd) ?? null;
                                                                const nextItemLv = itemLevels.find((lv) => lv.cd === L.itemLevel?.cd) ?? null;
                                                                setCustLevel(nextCustLv);
                                                                setItemLevel(nextItemLv);

                                                                const isNpiGroup = line.pairs.some((p) => typeof p.rule?.npi === "boolean");
                                                                if (isNpiGroup) {
                                                                    const custs = uniqByHrchyCd(line.pairs.map((p) => p.custHrchy));
                                                                    setSelectedCustHrchies(custs);
                                                                    setSelectedItemHrchies([]);
                                                                    setItemFilterMode("new");
                                                                    const npiVal = line.pairs.find((p) => typeof p.rule?.npi === "boolean")?.rule?.npi ?? true;
                                                                    setNpiChoice(npiVal);

                                                                    applyFilter((prev) => {
                                                                        const out = { ...prev };
                                                                        if (nextCustLv?.cd === CUSTOMER.cd) {
                                                                            out.customerHrchys = custs;
                                                                            out.customerCheckSelected = custs;
                                                                        } else {
                                                                            delete out.customerHrchys;
                                                                            delete out.customerCheckSelected;
                                                                        }
                                                                        out.category = [];
                                                                        out.itemCheckSelected = [];
                                                                        return out;
                                                                    });
                                                                } else {
                                                                    const custs = uniqByHrchyCd(line.pairs.map((p) => p.custHrchy));
                                                                    const items = uniqByHrchyCd(line.pairs.map((p) => p.itemHrchy));

                                                                    setSelectedCustHrchies(custs);
                                                                    setSelectedItemHrchies(items);
                                                                    setItemFilterMode("hierarchy");

                                                                    applyFilter((prev) => {
                                                                        const out = { ...prev };
                                                                        if (nextCustLv?.cd === CUSTOMER.cd) {
                                                                            out.customerHrchys = custs;
                                                                            out.customerCheckSelected = custs;
                                                                        } else {
                                                                            delete out.customerHrchys;
                                                                            delete out.customerCheckSelected;
                                                                        }
                                                                        if (nextItemLv?.cd === ITEM.cd) {
                                                                            out.category = items;
                                                                            out.itemCheckSelected = items;
                                                                        } else {
                                                                            delete out.category;
                                                                            delete out.itemCheckSelected;
                                                                        }
                                                                        return out;
                                                                    });
                                                                }

                                                                setBulkModels(g.models ?? []);
                                                            }}
                                                            sx={{ p: 0.25, borderRadius: 1, flex: 1, minWidth: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, "&:hover": { bgcolor: "action.hover" } }}
                                                        >
                                                            {custLabels.map((name, i) => (
                                                                <Chip key={`c-${name}-${i}`} size="small" label={name} />
                                                            ))}
                                                            <Typography variant="caption" sx={{ mx: 0.25 }}>×</Typography>
                                                            {itemLabels.slice(0, 6).map((name, i) => (
                                                                <Chip key={`i-${name}-${i}`} size="small" label={name} variant="outlined" />
                                                            ))}
                                                            {itemLabels.length > 6 && <Chip size="small" label={`+${itemLabels.length - 6}`} />}
                                                        </ListItemButton>
                                                        <Typography variant="caption" sx={{ whiteSpace: "nowrap", color: noNpiItems ? "warning.main" : undefined }}>
                                                            {countText}
                                                        </Typography>
                                                        {hasDb && (
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                sx={{ whiteSpace: "nowrap" }}
                                                                onClick={() => openRuleMgmt(dbRows,
                                                                    `${custLabels.join(", ")} x ${itemLabels.join(", ")} = ${modelsLabel(g.models)}`)}
                                                            >
                                                                {t("detail")}
                                                            </Button>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            aria-label="delete combo line"
                                                            onClick={() => {
                                                                // 이 행(조합)의 룰 삭제 — 저장된 행은 DB 도 함께 제거
                                                                const rules = new Set(line.pairs.map((p) => p.rule));
                                                                const removeLocal = () => setBfModelRules((prev) => prev.filter((r) => !rules.has(r)));
                                                                const ids = dbRows.map((r) => r.id);
                                                                if (ids.length) {
                                                                    bfAlgoRuleService.remove(ids).then(() => { removeLocal(); onRulesChanged?.(); });
                                                                } else {
                                                                    removeLocal();
                                                                }
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            });
                                        })()}

                                        {/* 같은 모델세트의 부분 커버(정확 지정) 조합 — n / 전체 표기 */}
                                        {(g.uploadCombos ?? []).map((c) => (
                                            <Box key={c.key} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                <Chip size="small" label={c.custLabel} />
                                                <Typography variant="caption" sx={{ mx: 0.25 }}>×</Typography>
                                                <Chip size="small" label={c.itemLabel} variant="outlined" />
                                                <Box sx={{ flex: 1 }} />
                                                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                                    {c.total != null ? `${c.rows.length} / ${c.total}` : c.rows.length}
                                                </Typography>
                                                <Button size="small" variant="text" onClick={() => openRuleMgmt(c.rows, `${c.custLabel} x ${c.itemLabel} = ${modelsLabel(g.models)}`)}>
                                                    {t("detail")}
                                                </Button>
                                                <IconButton size="small" onClick={() => handleDeleteUploadGroup(c.rows)} aria-label="delete combo rows">
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            })}

                            {/* 정확 지정 룰(CSV 가져오기 유래) — UI 카드와 동일하게 모델 칩 + 상위 조합
                                칩으로 표시하되, leftover 는 부분 커버이므로 조합별 건수(n / 전체)와
                                세부사항을 함께 보여준다. 전체 커버 조합은 롤업되어 일반 카드로 이미 복원됨 */}
                            {L.uploadGroups.map((u) => (
                                <Box
                                    key={`upload|${u.models}`}
                                    sx={{ borderRadius: 1.5, border: (th) => `1px solid ${th.palette.divider}`, p: 1, mb: 1 }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {(u.models || "").split(";").filter(Boolean).map((cd) => (
                                                <Chip key={cd} size="small" label={algoByCd[cd]?.name ?? cd} sx={chipSx(algoByCd[cd]?.name)} />
                                            ))}
                                        </Stack>
                                        <IconButton size="small" onClick={() => handleDeleteUploadGroup(u.rows)} aria-label="delete upload group">
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Box sx={{ mt: 1 }}>
                                        {u.combos.map((c) => (
                                            <Box key={c.key} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
                                                <Chip size="small" label={c.custLabel} />
                                                <Typography variant="caption" sx={{ mx: 0.25 }}>×</Typography>
                                                <Chip size="small" label={c.itemLabel} variant="outlined" />
                                                <Box sx={{ flex: 1 }} />
                                                <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                                                    {c.total != null ? `${c.rows.length} / ${c.total}` : c.rows.length}
                                                </Typography>
                                                <Button size="small" variant="text" onClick={() => openRuleMgmt(c.rows, `${c.custLabel} x ${c.itemLabel} = ${modelsLabel((u.models || "").split(";").filter(Boolean))}`)}>
                                                    {t("detail")}
                                                </Button>
                                                <IconButton size="small" onClick={() => handleDeleteUploadGroup(c.rows)} aria-label="delete combo rows">
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </ListItem>
                    ))}
                </List>
                {groupsByLevel.length === 0 && (
                    <Typography variant="caption" sx={{ display: "block", color: "text.disabled", px: 0.5, py: 0.25 }}>
                        {t("MSG_NoRuleForLevel")}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default withTranslation()(BfAlgorithmSettings);
